import { GlideDateTime } from "../../date-time/glide-date-time";
import { assert, panic } from "../Support";

export type BasePrimitiveValue = string | number | boolean;
export type PrimitiveValue = BasePrimitiveValue | Date | GlideDateTime | undefined;
export interface ObjectValue<O, A> {
    [key: string]: ValueProducer<O, A>;
}
export type Value<O, A> =
    | PrimitiveValue
    | Callable<O, A>
    | CapturedEvalEnvironment<O, A>
    | BindEnvironmentEvaluable<O, A>
    | ValueProducer<O, A>[]
    | ObjectValue<O, A>
    | O;

export const LOADING = null;
export type Loading = typeof LOADING;

export type PotentialValue<O, A> = Value<O, A> | Loading;

export function isBasePrimitiveValue(v: unknown): v is BasePrimitiveValue {
    const t = typeof v;
    if (t === "string") return true;
    if (t === "number") return true;
    if (t === "boolean") return true;
    return false;
}

export function isPrimitiveValue(v: unknown): v is PrimitiveValue {
    if (v === undefined) return true;
    if (isBasePrimitiveValue(v)) return true;
    if (v instanceof Date) return true;
    if (v instanceof GlideDateTime) return true;
    return false;
}

// This just excludes values we particularly don't want.  You still have to
// make sure that you're only writing primitive values, or converted
// GlideDates, for example.
export function isValueAllowedInAction(v: unknown): boolean {
    if (v === undefined) return false;
    if (v === null) return false;
    if (typeof v === "number" && isNaN(v)) return false;
    return true;
}

let isOtherValueFunction: (<O, A>(v: PotentialValue<O, A>) => boolean) | undefined;
export let makeContextBoundFunction: (<O, A>(v: Value<O, A>, ctx: Context<O, A>) => PotentialValue<O, A>) | undefined;
// FIXME: This isn't correctly typed.  In reality, when calling this functions,
// `O` and `A` will be fixed to concrete types.  This is probably not possible
// to express in the types system.
export function initComputation(
    isOtherValue: <O, A>(v: PotentialValue<O, A>) => boolean,
    makeContextBound: <O, A>(v: Value<O, A>, ctx: Context<O, A>) => PotentialValue<O, A>
): void {
    assert(isOtherValueFunction === undefined && makeContextBoundFunction === undefined);
    isOtherValueFunction = isOtherValue;
    makeContextBoundFunction = makeContextBound;
}

export function isObjectValue<O, A>(v: PotentialValue<O, A>): v is ObjectValue<O, A> {
    if (v === LOADING) return false;
    if (isPrimitiveValue(v)) return false;
    if (Array.isArray(v)) return false;
    if (v instanceof Callable) return false;
    if (v instanceof CapturedEvalEnvironment) return false;
    if (v instanceof BindEnvironmentEvaluable) return false;
    if (isOtherValueFunction !== undefined) {
        if (isOtherValueFunction(v)) return false;
    } else {
        return panic("setIsOtherValue was never called");
    }
    // setIsOtherValue must be:
    // if (v instanceof UI || v instanceof Action) return true;
    return true;
}

export function checkObjectValue<O, A>(v: PotentialValue<O, A>): ObjectValue<O, A> {
    if (!isObjectValue(v)) {
        return panic(`Value is not an object: ${v}`);
    }
    return v;
}

export interface ValueListener<O, A> {
    setDirty(sender: ValueProducer<O, A>): void;
}

export interface ForceValueProducer<O, A> {
    (vp: ValueProducer<O, A>): PotentialValue<O, A>;
}

export interface ObjectPropertyGetter<T> {
    getProperty(name: string): T | undefined;
}

export interface ObjectInspector<O, A> extends ObjectPropertyGetter<ValueProducer<O, A>> {
    getObjectValue(): ObjectValue<O, A>;
}

export interface ValueProducer<O, A> {
    readonly current: PotentialValue<O, A>;
    // If this returns `false` then the value definitely wasn't changed.
    // If it return `true`, the value might have been changed.
    set(v: Value<O, A>): boolean;

    // These can only be called if the value is an object
    addProperty(name: string, vp: ValueProducer<O, A>): void;
    removeProperty(name: string): void;

    // These can only be called if the value is an array
    appendItem(vp: ValueProducer<O, A>): void;
    removeLastItem(): void;
    removeItemAtIndex(index: number): void;
    removeItem(item: ValueProducer<O, A>): void;

    // These can only be called if the parent is an array
    removeFromParent(): void;

    subscribe(l: ValueListener<O, A>): boolean;
    unsubscribe(l: ValueListener<O, A>): void;

    getObjectInspector(force: ForceValueProducer<O, A> | undefined): ObjectInspector<O, A> | Loading | undefined;
}

export type ValueProducers<O, A> = readonly (ValueProducer<O, A> | undefined)[];

export type Context<O, A> = ValueProducer<O, A>;

export interface Evaluable<O, A> {
    evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A>;
}

export interface EnvironmentBoundEvaluable<O, A> extends Evaluable<O, A> {
    eval(context: Context<O, A>, actionSource?: ActionSource): ValueProducer<O, A>;
}

// FIXME: This shouldn't be here, but it's entangled with `Evaluable`.
export enum ActionSource {
    // FIXME: This is stupid.  The Clickable needs to take its action source
    // as an argument.
    Clickable = "clickable",
    ListItem = "list-item",
    GridItem = "grid-item",
    CalendarListItem = "calendar-list-item",
    ColumnItem = "column-item",
    GalleryItem = "gallery-item",
    Image = "image",
    Summary = "summary",
    OpenLink = "open-link",
    OpenMap = "open-map",
    SendEmail = "send-email",
    PostComment = "post-comment",
    DeleteComments = "delete-comments",
    Button = "button",
    Phone = "phone",
    TextMessage = "text-message",
    Note = "note",
    Signature = "signature",
    Switch = "switch",
    TextField = "text-field",
    PivotPicker = "pivot-picker",
    EmailAuthentication = "email-authentication",
    DateTimeField = "date-time-field",
    ImagePicker = "image-picker",
    FilePicker = "file-picker",
    BuyScreen = "buy-screen",
    UserProfile = "user-profile",
    MapScreen = "map-screen",
    Tinder = "tinder",
    Markdown = "markdown",
    None = "none",
}

export interface EvalEnvironment<O, A> {
    lookupUserDefinedComponent(name: string): Evaluable<O, A> | undefined;
    readonly root: RootEvalEnvironment<O, A>;
    get(name: string): EnvironmentBoundEvaluable<O, A> | undefined;
    readonly actionSource: ActionSource | undefined;
}

export abstract class UnsubscribableValueProducer<O, A> implements ValueProducer<O, A> {
    public abstract get current(): Value<O, A>;

    public set(_v: Value<O, A>): boolean {
        return panic("Cannot set an UnsubscribableValueProducer");
    }

    public addProperty(_name: string, _vp: ValueProducer<O, A>): void {
        return panic("Cannot add property to an UnsubscribableValueProducer");
    }

    public removeProperty(_name: string): void {
        return panic("Cannot remove property from an UnsubscribableValueProducer");
    }

    public appendItem(_vp: ValueProducer<O, A>): void {
        return panic("Cannot add item to an UnsubscribableValueProducer");
    }

    public removeLastItem(): void {
        return panic("Cannot remove an item from an UnsubscribableValueProducer");
    }

    public removeItem(_item: ValueProducer<O, A>): void {
        return panic("Cannot remove an item from an UnsubscribableValueProducer");
    }

    public removeFromParent(): void {
        return panic("Cannot remove an UnsubscribableValueProducer from its parent");
    }

    public removeItemAtIndex(_index: number): void {
        return panic("Cannot remove an item from an UnsubscribableValueProducer");
    }

    public subscribe(_l: ValueListener<O, A>): boolean {
        return false;
    }

    public unsubscribe(_l: ValueListener<O, A>): void {
        return;
    }

    public abstract getObjectInspector(
        force: ForceValueProducer<O, A> | undefined
    ): ObjectInspector<O, A> | Loading | undefined;
}

export class RootEvalEnvironment<O, A> implements EvalEnvironment<O, A> {
    public readonly actionSource: ActionSource | undefined = undefined;

    constructor(
        public readonly appEnvironment: A,
        private readonly _userDefinedComponents: ReadonlyMap<string, Evaluable<O, A>>
    ) {}

    public lookupUserDefinedComponent(name: string): Evaluable<O, A> | undefined {
        return this._userDefinedComponents.get(name);
    }

    public get root(): RootEvalEnvironment<O, A> {
        return this;
    }

    public get(_name: string): EnvironmentBoundEvaluable<O, A> | undefined {
        return undefined;
    }
}

export class CapturedEvalEnvironment<O, A> implements EvalEnvironment<O, A> {
    constructor(private readonly _env: EvalEnvironment<O, A>) {}

    public get actionSource(): ActionSource | undefined {
        return this._env.actionSource;
    }

    public lookupUserDefinedComponent(name: string): Evaluable<O, A> | undefined {
        return this._env.lookupUserDefinedComponent(name);
    }

    public get root(): RootEvalEnvironment<O, A> {
        return this._env.root;
    }

    public get(name: string): EnvironmentBoundEvaluable<O, A> | undefined {
        return this._env.get(name);
    }
}

class ActionSourceEvalEnvironment<O, A> implements EvalEnvironment<O, A> {
    constructor(public readonly actionSource: ActionSource, private readonly _next: EvalEnvironment<O, A>) {}

    public lookupUserDefinedComponent(name: string): Evaluable<O, A> | undefined {
        return this._next.lookupUserDefinedComponent(name);
    }

    public get root(): RootEvalEnvironment<O, A> {
        return this._next.root;
    }

    public get(name: string): EnvironmentBoundEvaluable<O, A> | undefined {
        return this._next.get(name);
    }
}

export class BindEnvironmentEvaluable<O, A> implements EnvironmentBoundEvaluable<O, A> {
    constructor(private readonly _evaluable: Evaluable<O, A>, private readonly _env: EvalEnvironment<O, A>) {}

    public eval(context: Context<O, A>, actionSource: ActionSource | undefined): ValueProducer<O, A> {
        let env = this._env;
        if (actionSource !== undefined) {
            env = new ActionSourceEvalEnvironment(actionSource, env);
        }
        return this._evaluable.evalInEnvironment(env, context);
    }

    public evalInEnvironment(_env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return this.eval(context, undefined);
    }
}

export abstract class Callable<O, A> extends UnsubscribableValueProducer<O, A> implements Evaluable<O, A> {
    public abstract call(
        args: EnvironmentBoundEvaluable<O, A>[],
        context: Context<O, A>,
        rootEnv: RootEvalEnvironment<O, A>
    ): ValueProducer<O, A>;

    public get current(): Value<O, A> {
        return this;
    }

    public evalInEnvironment(_env: EvalEnvironment<O, A>, _context: Context<O, A>): ValueProducer<O, A> {
        return this;
    }

    public getObjectInspector(): ObjectInspector<O, A> | Loading | undefined {
        return undefined;
    }
}

type BuilderDeserializableKind = "ui" | "action";
export type DeserializableKind = BuilderDeserializableKind | "evaluable" | "callable";
export type ArgumentKind =
    | DeserializableKind
    | "json"
    | "constant-json"
    | "bindings"
    | "boolean"
    | "string"
    | "string-array"
    | "kvp-array"
    | "evaluable-array"
    | "string-json-array";

// [name, kind, isRequired].  If it's just a single string, it's a required "evaluable".
export type ArgumentSpec = string | [string, ArgumentKind] | [string, ArgumentKind, boolean];

export type DeserializationSpec<TKind extends string> = [string, TKind, ...ArgumentSpec[]];

export interface DeserializableClass<O, A> {
    new (...args: any[]): Evaluable<O, A>;
    spec: DeserializationSpec<DeserializableKind>;
}
